import { useTheme } from '@mui/material';
import {
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { TableSkeletonLoader } from '../../../components/SkeletonLoader/Table.SkeletonLoader';
import { TableV1 } from '../../../components/Table/TableV1';
import { ROUTES } from '../../../constants/routes';
import { useTimesheets } from '../../../queries/hooks/useTimesheets';
import { userState } from '../../../state/UIState';
import { UserRole } from '../../../types';
import { hasRole } from '../../../utils/roles';
import { mapTsStatuses } from '../../../utils/tsMappers';
import { useOpAllTimesheetsTableColumns } from '../hooks/useOpAllTimesheetsTableColumns';
import { useProxyAllTimesheetsTableColumns } from '../hooks/useProxyAllTimesheetsTableColumns';
import { FilterSection } from './FilterSection';

export function AllTimesheetsTable() {
  const { colors } = useTheme();
  const user = useAtomValue(userState);
  const { pathname } = useLocation();

  // The view is driven by the ROUTE, not by role precedence: the Submitter and Proxy "All Timesheets"
  // menu items now have distinct routes, so a user holding both roles can switch between the two.
  // Defense-in-depth: a Proxy-only user has no Submitter dataset, so if they ever land on the shared
  // /all-timesheets list (e.g. via an older breadcrumb) we fall back to the Proxy view instead of
  // requesting the Operating Partner dataset they don't hold (→ 403 → endless skeleton).
  const isProxyRoute = pathname.includes(ROUTES.PROXY_ALL_TIMESHEETS);
  const isProxyView =
    isProxyRoute || (!hasRole(user, UserRole.OPERATING_PARTNER) && hasRole(user, UserRole.PROXY));
  const roleContext = isProxyView ? UserRole.PROXY : UserRole.OPERATING_PARTNER;

  const { isLoading: areAllTimesheetsLoading, data: allTimesheets } = useTimesheets({
    userId: user?.id,
    role: roleContext,
  });

  const statusMappedData = useMemo(() => {
    if (!allTimesheets) return [];
    return mapTsStatuses(allTimesheets, UserRole.OPERATING_PARTNER);
  }, [allTimesheets]);

  // Call BOTH column hooks unconditionally (Rules of Hooks): the two "All Timesheets" routes reuse the
  // same component instance, so conditionally choosing WHICH hook to call would change hook order on
  // route switch and crash the page. We just pick the resulting columns by view.
  const opColumns = useOpAllTimesheetsTableColumns();
  const proxyColumns = useProxyAllTimesheetsTableColumns();
  const columns = isProxyView ? proxyColumns : opColumns;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    data: statusMappedData ?? [],
    columns,
    state: {
      sorting,
      columnFilters,
    },
    enableSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (item) => String(item.id),
  });

  if (areAllTimesheetsLoading || !allTimesheets) return <TableSkeletonLoader />;

  return (
    <div>
      <FilterSection table={table} columnFilters={columnFilters} isProxyView={isProxyView} />
      <TableV1
        table={table}
        height='auto'
        wrapperStyle={{
          maxHeight: 'calc(100vh - 288px)',
          overflowX: 'scroll',
          border: `1px solid ${colors.border.default}`,
        }}
      />
    </div>
  );
}
